import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pizza, KeyRound, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";
import { evaluatePassword } from "../lib/passwordPolicy";

export default function ChangePassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { valid: pwValid } = evaluatePassword(newPw);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pwValid) {
      setError(t("security.policyHint"));
      return;
    }
    if (newPw !== confirm) {
      setError(t("changePassword.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      // Refresh user info so must_change_password flips to false
      if (refreshUser) await refreshUser();
      toast.success(t("changePassword.success"));
      navigate("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || t("common.error");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
            <Pizza className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2.5} />
          </div>
          <div className="font-display font-extrabold text-xl">Chizy Chiz</div>
        </div>

        <h2 className="font-display font-extrabold text-2xl text-[#1A1A1A] tracking-tight flex items-center gap-2">
          <KeyRound className="w-6 h-6 text-amber-500" />
          {t("changePassword.title")}
        </h2>
        <p className="text-slate-500 mt-1.5 text-sm">
          {user?.must_change_password ? t("changePassword.firstTime") : t("changePassword.subtitle")}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">{t("changePassword.oldPassword")}</label>
            <input
              data-testid="cp-old-password"
              type="password"
              value={oldPw}
              onChange={(e) => setOldPw(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">{t("changePassword.newPassword")}</label>
            <input
              data-testid="cp-new-password"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]"
              required
              minLength={10}
            />
            <PasswordStrengthMeter password={newPw} />
          </div>
          <div>
            <label className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">{t("changePassword.confirm")}</label>
            <input
              data-testid="cp-confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]"
              required
              minLength={8}
            />
          </div>

          {error && (
            <div data-testid="cp-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
              {error}
            </div>
          )}

          <button
            data-testid="cp-submit"
            type="submit"
            disabled={loading || !oldPw || !pwValid || !confirm}
            className="w-full btn-primary-chizy py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? t("common.saving") : (
              <>{t("changePassword.submit")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} /></>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
